import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserSpaces } from '../api/spaceApi';
import { getUnreadCountBySpaces } from '../api/notificationApi';
import Logo from '../componets/Logo';
import { SquarePen, SquareX } from 'lucide-react';
import SpaceForm from '../componets/SpaceForm';
import { Helmet } from 'react-helmet-async';

interface Space {
  id: string;
  title: string;
  ownerName: string;
  location: string;
  tags: string[];
  reservationCount: number;
  price: number;
  description: string;
  main_image?: string;
}

const MySpaces: React.FC = () => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';

  const navigate = useNavigate();
  const [mySpaces, setMySpaces] = useState<Space[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unreadBySpaces, setUnreadBySpaces] = useState<{ [key: string]: number }>({});

  // 모달 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'delete' | 'edit' | null>(null);
  const [targetSpaceId, setTargetSpaceId] = useState<string | null>(null);
  const [checklist, setChecklist] = useState({ item1: false, item2: false });
  const [editData, setEditData] = useState<Space | null>(null);

  useEffect(() => {
    const fetchSpaces = async () => {
      try {
        const spaces = await getUserSpaces();
        setMySpaces(spaces);

        const unread = await getUnreadCountBySpaces();
        setUnreadBySpaces(unread);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSpaces();
  }, []);

  const handleEditClick = (spaceId: string) => {
    const target = mySpaces.find((s) => s.id === spaceId);
    if (!target) return;
  
    setTargetSpaceId(spaceId);
    setEditData(target); // ← 여기 추가
    setModalType('edit');
    setIsModalOpen(true);
  };

  const handleDeleteClick = (spaceId: string) => {
    const targetSpace = mySpaces.find((s) => s.id === spaceId);
    if (targetSpace && targetSpace.reservationCount > 0) {
      alert('진행 중인 예약이 존재합니다.');
      return;
    }
    setTargetSpaceId(spaceId);
    setModalType('delete');
    setIsModalOpen(true);
  };

  const handleChecklistChange = (key: keyof typeof checklist) => {
    setChecklist((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const confirmDelete = async () => {
    if (!targetSpaceId) return;

    try {
      await fetch(`${API_BASE_URL}/space/${targetSpaceId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('user_token')}`,
        },
        body: JSON.stringify({ status: 'DELETED' }),
      });

      setMySpaces((prev) => prev.filter((s) => s.id !== targetSpaceId));
      setIsModalOpen(false);
      setChecklist({ item1: false, item2: false });
    } catch (err: any) {
      alert(err.response?.data?.message || '삭제 실패');
    }
  };

  if (loading) return <div className="min-h-screen p-6">Loading...</div>;
  if (error) return <div className="min-h-screen p-6 text-red-500">{error}</div>;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Helmet>
        <title>내 공간 | BIN-GO</title>
        <meta name="description" content="내가 등록한 공간을 관리하는 페이지입니다." />
        <meta property="og:title" content="내 공간 | BIN-GO" />
        <meta property="og:description" content="내가 올린, 소유한 공간 리스트 관리." />
      </Helmet>
      <Logo />
      <h1 className="text-2xl font-semibold mb-6 mt-6">내 공간</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {mySpaces.map((space) => {
          const hasUnread = unreadBySpaces[space.id] > 0;

          return (
            <div
              key={space.id}
              className={`rounded shadow overflow-hidden relative ${
                hasUnread ? 'border-2 border-green-500' : 'border border-gray-200'
              }`}
            >
              {/* 카드 상단 이미지 */}
              <div
                className="h-48 w-full bg-gray-200 overflow-hidden cursor-pointer"
                onClick={() => navigate(`/page/ReservationList/${space.id}`)}
              >
                {space.main_image ? (
                  <img
                    src={space.main_image}
                    alt={space.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    No Image
                  </div>
                )}
              </div>
              {/* 카드 상단 편집/삭제 아이콘 */}
              <div className="p-4 flex flex-col space-y-2 relative">
              <div className="absolute top-2 right-2 flex space-x-2">
                <SquarePen
                  className="text-gray-600 hover:text-blue-600 cursor-pointer w-5 h-5"
                  onClick={() => handleEditClick(space.id)}
                />
                <SquareX
                  className="text-red-500 hover:text-red-700 cursor-pointer w-5 h-5"
                  onClick={() => handleDeleteClick(space.id)}
                />
              </div>

              {/* 카드 내용 */}
              <div className="p-4 flex flex-col space-y-2">
                <h3 className="font-semibold text-lg">{space.title}</h3>
                <p>{space.location}</p>
                <p className="text-sm text-blue-500">{space.tags.join(', ')}</p>
                <p>예약 현황: {space.reservationCount}</p>
                <p>요금: {space.price.toLocaleString()} (/일)</p>

                <button
                  onClick={() => navigate(`/page/ReservationList/${space.id}`)}
                  className="relative bg-blue-500 text-white px-4 py-2 rounded mt-2"
                >
                  예약 현황
                  {hasUnread && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white" />
                  )}
                </button>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {/* 모달 */}
      {isModalOpen && modalType === 'delete' && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow-lg w-96">
            <h3 className="text-xl font-semibold mb-4">공간 삭제 확인</h3>
            <textarea
              readOnly
              className="w-full p-2 border rounded bg-gray-100 text-sm mb-4"
              rows={4}
              value="삭제는 복구할 수 없습니다. 아래 항목을 확인 후 진행해 주세요."
            />
            <div className="mb-4">
              {['안내사항에 동의합니다.', '삭제 후에는 복구할 수 없습니다.'].map((text, i) => {
                const key = `item${i + 1}` as keyof typeof checklist;
                return (
                  <label key={key} className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      checked={checklist[key]}
                      onChange={() => handleChecklistChange(key)}
                      className="mr-2"
                    />
                    {text}
                  </label>
                );
              })}
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400"
              >
                취소
              </button>
              <button
                onClick={confirmDelete}
                disabled={!checklist.item1 || !checklist.item2}
                className={`px-4 py-2 rounded text-white ${
                  checklist.item1 && checklist.item2
                    ? 'bg-red-500 hover:bg-red-600'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && modalType === 'edit' && editData && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md relative">
          {/* 모달 닫기 버튼 */}
          <button
            onClick={() => setIsModalOpen(false)}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 font-bold text-lg"
          >
            ✕
          </button>
            <h3 className="text-xl font-semibold mb-4">공간 수정</h3>
            <SpaceForm
              initialData={{
                id: editData.id,
                title: editData.title,
                description: editData.description,
                location: editData.location,
                pricePerDay: editData.price,
                maxDurationDays: 30, // 필요 시 서버 데이터로
                main_image: editData.main_image,
                tags: editData.tags.map((_, i) => i), // 실제 ID 매핑 필요
                regionId: 0, // 실제 데이터 매핑 필요
              }}
              onSubmitSuccess={() => setIsModalOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MySpaces;
